// @ts-nocheck

import _ from 'lodash';

import { type BasketState } from '../../../states/basket';
import { type BasketItemState } from '../../../states/basket';
import { type MatchState } from '../../../states/entities/matches';
import { BasketStatuses } from '../../../types/basket';
import { type MPState } from '../reducers/mp';
import { selectIsMPEnabled } from '../selectors/computed/runtime';
import { selectIsMPUserOptIn } from '../selectors/user';
import { MPCardStatuses } from '../types/cards';
import { MPFamilyStatuses } from '../types/families';

import getEligibleCards from './getEligibleCards';

const getIsMatchNotAlreadyInPendingBetslip = (matchState: MatchState = {}, mpState: MPState = {}) => {
  const { matchId } = matchState;
  const { user: { betslips: mpUserBetslips = {}, progress: mpUserProgress = {} } = {} } = mpState;

  const userProgressPendingBetslipReferences = Object.keys(mpUserProgress).reduce((acc, familyId) => {
    const familyInfos = mpUserProgress[familyId] ?? {};

    const { status, cards = {} } = familyInfos;
    const cardIds = Object.keys(cards);

    if (status === MPFamilyStatuses.OPEN && cardIds.length) {
      return [
        ...acc,
        ...cardIds.reduce(
          (_acc, cardId) =>
            cards[cardId].status === MPCardStatuses.PENDING ? [..._acc, cards[cardId].bet?.betslipReference] : _acc,
          [],
        ),
      ];
    }

    return acc;
  }, []);

  return !userProgressPendingBetslipReferences.some(
    (betslipRef) => !!~mpUserBetslips[betslipRef].matchIds.indexOf(matchId),
  );
};

const getIsMatchStartDateValid = (matchState: MatchState) => {
  if (matchState) {
    const { matchStart = Infinity } = matchState;

    return matchStart * 1000 - Date.now() < 1000 * 60 * 60 * 24 * 2; // match's start date must be in less than 48h
  }

  return false;
};

const getIsBasketItemReadyForEligibilityCheck = (
  basketItem?: BasketItemState,
  matchesState: { [matchId: string]: MatchState } = {},
  mpState: MPState,
) => {
  const { matchId } = basketItem ?? {};
  const match = matchesState[matchId] ?? {};

  const isMatchStartDateValid = getIsMatchStartDateValid(match);
  const isMatchNotAlreadyInPendingBetslip = getIsMatchNotAlreadyInPendingBetslip(match, mpState);

  return isMatchStartDateValid && isMatchNotAlreadyInPendingBetslip;
};

const getIsBasketReadyForEligibilityCheck = (
  { items: basketItems }: BasketState = {},
  matchesState: { [matchId: string]: MatchState } = {},
  mpState: MPState,
) =>
  Object.keys(basketItems).every((itemId) =>
    getIsBasketItemReadyForEligibilityCheck(basketItems[itemId] ?? {}, matchesState, mpState),
  );

const recomputeNextMPBasketState = (nextBasketState, previousGlobalState) => {
  const { isCashout, status: basketStatus } = nextBasketState;
  const isMPEnabled = selectIsMPEnabled(previousGlobalState);
  const isUserOptIn = selectIsMPUserOptIn(previousGlobalState);

  if (!isMPEnabled || !isUserOptIn || isCashout || basketStatus === BasketStatuses.VALIDATED) {
    return nextBasketState;
  }

  const {
    entities: { matches: previousMatches = {} } = {},
    basket: previousBasket = {},
    mp: previousMPState,
  } = previousGlobalState ?? {};

  const tmpBasketState = nextBasketState ?? previousBasket;
  const isBasketReadyForEligibilityCheck = getIsBasketReadyForEligibilityCheck(
    nextBasketState,
    previousMatches,
    previousMPState,
  );

  let nextBasketMonopolyCard = null;

  if (isBasketReadyForEligibilityCheck) {
    nextBasketMonopolyCard =
      getEligibleCards({
        ...previousGlobalState,
        basket: tmpBasketState,
      })?.[0] ?? null;
  }

  const hasBasketMonopolyCardChanged = !_.isEqual(nextBasketMonopolyCard, tmpBasketState.monopolyCard);
  let hasBasketItemsChanged = false;

  const { items: basketItems } = tmpBasketState;
  const resetedBasketItems = {};

  for (const itemId of Object.keys(basketItems)) {
    resetedBasketItems[itemId] = basketItems[itemId];
    delete resetedBasketItems[itemId].monopolyCard;
  }

  const nextItems = { ...resetedBasketItems };

  const sortingCallback = (a, b) => a.sortBy - b.sortBy;

  const basketItemsAsArr = Object.keys(resetedBasketItems)
    .map((itemId) => resetedBasketItems[itemId])
    .sort(sortingCallback);

  for (const basketItem of basketItemsAsArr) {
    const nextBasketItemsAsArr = Object.keys(nextItems)
      .map((itemId) => nextItems[itemId])
      .sort(sortingCallback);
    const { oddId: itemId } = basketItem;

    const isBasketItemReadyForEligibilityCheck = getIsBasketItemReadyForEligibilityCheck(
      basketItem,
      previousMatches,
      previousMPState,
    );

    let eligibleCards = [];

    if (isBasketItemReadyForEligibilityCheck) {
      eligibleCards = getEligibleCards(
        {
          ...previousGlobalState,
          basket: {
            ...tmpBasketState,
            nextItems,
          },
        },
        itemId,
      );
    }

    if (eligibleCards.length) {
      const eligibleCardThatFits = eligibleCards.find(
        (card) =>
          !nextBasketItemsAsArr
            .filter((basketItem) => !!basketItem.monopolyCard)
            .some(
              (basketItem) =>
                basketItem.monopolyCard.index === card.index && basketItem.monopolyCard.familyId === card.familyId,
            ),
      );

      if (eligibleCardThatFits) {
        nextItems[itemId] = {
          ...nextItems[itemId],
          monopolyCard: eligibleCardThatFits,
        };

        hasBasketItemsChanged = true;
      } else {
        delete nextItems[itemId].monopolyCard;
        hasBasketItemsChanged = true;
      }
    } else {
      delete nextItems[itemId].monopolyCard;
      hasBasketItemsChanged = true;
    }
  }

  let nextState = tmpBasketState;

  if (hasBasketMonopolyCardChanged) {
    nextState = { ...nextState, monopolyCard: nextBasketMonopolyCard };
  }

  if (hasBasketItemsChanged) {
    nextState = { ...nextState, items: nextItems };
  }

  return nextState;
};

export default recomputeNextMPBasketState;
